import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import LoadingSection from "../../helperComponents/LoadingSection";
import { serverLine } from "../../../controllers/network/serverLine";
import CustomLabel from "../CustomLabel";
import DailyResponsibilityField from "./DailyResponsibilityField";
import { useTranslation } from "react-i18next";
import PrimaryButton from "../../helperComponents/PrimaryButton";
import CustomLabelDim from "../CustomLabelDim";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 25px;
  width: 100%;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid var(--borderDim);
  background: var(--surface2);
`;

const Filters = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex-wrap: wrap;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const Content = styled.div`
  border-radius: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 500px;
  animation: centerScaleReveal 0.4s ease-in-out forwards;
`;

const ROW_DEFINITIONS = [
  [
    { key: "commander", label: "PC" },
    { key: "commander2", label: "PC 2" },
  ],
  [{ key: "guardWithGun", label: "Armement" }],
  [
    { key: "supervisor", label: "Superviseur" },
    { key: "supervisor2", label: "Superviseur" },
  ],
  [
    { key: "screeningGuardMorning1", label: "Filtrage (Jour)" },
    { key: "screeningGuardMorning2", label: "Filtrage 2" },
  ],
  [{ key: "fregate", label: "Fregate" }],
  [
    { key: "eclair1", label: "Eclair" },
    { key: "eclair2", label: "Eclair 2" },
    { key: "eclair3", label: "Eclair 3" },
  ],
  [
    { key: "mobileGuardA1", label: "MOBILE ZOULOU 6" },
    { key: "surveillanceGuard1", label: "Mike 2" },
    { key: "surveillanceGuard2", label: "Mike 6" },
    { key: "other", label: "Autre" },
  ],
  [{ key: "mobileGuardA2", label: "MOBILE ZOULOU 6" }],
  [{ key: "mobileGuardA3", label: "MOBILE ZOULOU 6" }],
  [
    { key: "bassamGuard", label: "Mike Bassam" },
    { key: "aboissoGuard", label: "Mike Aboisso" },
    { key: "adzopeGuard", label: "Mike Adzope" },
  ],
  [
    { key: "mobileGuardB1", label: "MOBILE ZOULOU 10" },
    { key: "mobileGuardC1", label: "MOBILE ZOULOU 20" },
    { key: "surveillanceGuard3", label: "Mike 9" },
    { key: "surveillanceGuard4", label: "Mike 10" },
  ],
  [
    { key: "mobileGuardB2", label: "MOBILE ZOULOU 10" },
    { key: "mobileGuardC2", label: "MOBILE ZOULOU 20" },
  ],
  [
    { key: "mobileGuardB3", label: "MOBILE ZOULOU 10" },
    { key: "mobileGuardC3", label: "MOBILE ZOULOU 20" },
  ],
  [
    { key: "surveillanceGuard5", label: "Mike 11" },
    { key: "surveillanceGuard6", label: "Mike 12" },
    { key: "surveillanceGuard7", label: "Mike 20" },
    { key: "surveillanceGuard8", label: "Mike 21" },
  ],
  [
    { key: "surveillanceGuard9", label: "Mike 22" },
    { key: "songonGuard", label: "Mike Songon" },
  ],
];

const FIELD_KEYS = ROW_DEFINITIONS.flat().map((item) => item.key);

export default function DailyResponsibilityCore({
  date,
  shift,
  title,
  filters,
  hideAllFilters,
  readOnly,
}) {
  const { t } = useTranslation();
  const [state, setState] = useState({});
  const [loadedRawState, setLoadedRawState] = useState({});
  const [loading, setLoading] = useState(true);

  const knownKeysSet = useMemo(() => new Set(FIELD_KEYS), []);

  const doQuery = useCallback(() => {
    if (!date) return;

    setLoading(true);
    serverLine
      .get(`/daily-responsibility/?date=${date}&shift=${shift}`)
      .then((newData) => {
        const raw = newData?.responsibility || {};
        setLoadedRawState(raw);
        setState(normalizeResponsibility(raw));
      })
      .finally(() => setLoading(false));
  }, [date, shift]);

  useEffect(() => {
    doQuery();
  }, [doQuery]);

  let core = null;

  if (loading && date && shift) {
    core = <LoadingSection />;
  } else {
    core = (
      <>
        {ROW_DEFINITIONS.map((row, rowIndex) => (
          <Row key={`row-${rowIndex}`}>
            {row.map((fieldDef) => (
              <DailyResponsibilityField
                key={fieldDef.key}
                label={fieldDef.label}
                readOnly={readOnly}
                values={state[fieldDef.key] || [""]}
                onChangeValue={(index, value) =>
                  updateValue(fieldDef.key, index, value)
                }
                onAdd={() => addField(fieldDef.key)}
                onRemove={(index) => removeField(fieldDef.key, index)}
              />
            ))}
          </Row>
        ))}
      </>
    );
  }

  if (!date) {
    core = <CustomLabelDim>Veuillez sélectionner la date</CustomLabelDim>;
  }

  return (
    <Container>
      {title ? <CustomLabel>{title}</CustomLabel> : null}
      {hideAllFilters ? null : <Filters>{filters}</Filters>}
      <Content>{core}</Content>
      <br />
      {readOnly ? null : (
        <PrimaryButton onClick={save}>Enregister</PrimaryButton>
      )}
    </Container>
  );

  function updateValue(fieldKey, valueIndex, value) {
    setState((prev) => {
      const currentValues = Array.isArray(prev[fieldKey]) ? [...prev[fieldKey]] : [""];
      currentValues[valueIndex] = value;
      return {
        ...prev,
        [fieldKey]: currentValues,
      };
    });
  }

  function addField(fieldKey) {
    setState((prev) => {
      const currentValues = Array.isArray(prev[fieldKey]) ? [...prev[fieldKey]] : [""];
      return {
        ...prev,
        [fieldKey]: [...currentValues, ""],
      };
    });
  }

  function removeField(fieldKey, valueIndex) {
    setState((prev) => {
      const currentValues = Array.isArray(prev[fieldKey]) ? [...prev[fieldKey]] : [""];
      if (currentValues.length <= 1) return prev;
      currentValues.splice(valueIndex, 1);
      return {
        ...prev,
        [fieldKey]: currentValues,
      };
    });
  }

  function normalizeResponsibility(raw = {}) {
    const normalized = {};
    for (const key of FIELD_KEYS) {
      const rawValue = raw[key];
      if (Array.isArray(rawValue)) {
        normalized[key] = rawValue.length ? rawValue : [""];
      } else if (typeof rawValue === "string") {
        normalized[key] = [rawValue];
      } else if (rawValue === null || rawValue === undefined) {
        normalized[key] = [""];
      } else {
        normalized[key] = [String(rawValue)];
      }
    }
    return normalized;
  }

  function serializeStateForSave(normalizedState, originalRawState) {
    const payload = {};

    // Keep unknown legacy keys untouched.
    for (const key in originalRawState || {}) {
      if (!knownKeysSet.has(key)) {
        payload[key] = originalRawState[key];
      }
    }

    for (const key of FIELD_KEYS) {
      const values = (normalizedState[key] || [])
        .map((item) => (typeof item === "string" ? item.trim() : String(item || "").trim()))
        .filter(Boolean);

      payload[key] = values;
    }

    return payload;
  }

  async function save() {
    setLoading(true);
    try {
      await serverLine.patch(`/daily-responsibility/?`, {
        changes: serializeStateForSave(state, loadedRawState),
        date,
        shift,
      });
      window.popupAlert(t("done"));
      doQuery();
    } finally {
      setLoading(false);
    }
  }

}
